/**
 * ATL-001144 (SEC-035) — silent access-token renewal.
 *
 * Access tokens now live 15 minutes. Instead of signing the user out when one
 * expires, the client trades its refresh token for a new pair at
 * POST /auth/refresh and carries on. Three rules keep that safe alongside
 * refresh-token rotation with reuse detection (ATL-001151 / SEC-042), where
 * presenting an already-used refresh token revokes EVERY session of the user:
 *
 *  1. Single flight per session. Concurrent 401s in one tab share one
 *     refresh call (`inFlight`).
 *  2. One refresher across tabs. Tabs of the same session hold the same
 *     refresh token, so the refresh runs under a Web Lock, and inside it we
 *     first check whether another tab already renewed the token. Browsers
 *     without Web Locks fall back to re-reading storage after a lost race
 *     (the server answers a just-rotated token with a plain 401 for 10s).
 *  3. Shared storage. The admin app keeps its tokens in localStorage, so every
 *     tab sees a rotation. The customer / carrier portals keep their session
 *     in per-tab sessionStorage — and a duplicated tab copies it — so their
 *     renewed pair goes to a localStorage slot keyed by the session's ORIGINAL
 *     refresh token (the "lineage"), which every copy of that session shares.
 *
 * The portal pages keep rendering with the token they logged in with; the
 * axios request interceptor swaps in the latest token (`resolveRequestAuth`).
 * That way a renewal never changes React state, which would re-run effects
 * keyed on the token (the carrier portal reloads everything when it changes).
 *
 * A refresh is attempted when the server rejects the ACCESS TOKEN itself:
 * expired ("Token expired") or invalid/revoked ("Invalid token"). The latter
 * is what another device's logout produces (SEC-036 bumps the user's
 * tokenVersion). Logout is per device by design, so the other devices renew
 * with their own refresh token and stay signed in. Any other 401 (missing
 * header, a route's own check), a refused refresh, or a second 401 after the
 * retry signs the user out as before; the refresh token stays the authority.
 * A device whose refresh token was revoked (reuse detection, password reset)
 * therefore still ends up signed out.
 *
 * Impersonation sessions carry no refresh token and are deliberately not
 * renewable: they end when their access token expires.
 */
import axios, { isAxiosError } from 'axios';
import { API_BASE_URL } from './api-base-url';
import { decodeJwt, msUntilExpiry, recordServerClock } from './jwt';
import { useAuthStore } from '../stores/auth-store';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

interface StoredTokens {
  accessToken: string | null;
  refreshToken: string | null;
}

export interface SessionTokenStore {
  /** Identifies the session: in-tab single flight and cross-tab lock name. */
  key: string;
  read(): StoredTokens;
  write(tokens: TokenPair): void;
  /**
   * Never present `refreshToken` again: a refresh with it may have been
   * committed server-side without the client receiving the new pair.
   */
  forget(refreshToken: string): void;
  /** Extra fields for POST /auth/refresh (e.g. the admin's active org). */
  refreshBody?(): Record<string, unknown>;
}

/** Treat a token that expires within this window as already expired. */
const EXPIRY_SKEW_MS = 5_000;
/** Proactive renewal runs this long before the access token expires. */
const PROACTIVE_REFRESH_LEAD_MS = 60_000;
/** Fallback (no Web Locks): wait for the winning tab to store its new pair. */
const LOST_RACE_RECHECK_MS = 1_000;
const REFRESH_TIMEOUT_MS = 15_000;

const inFlight = new Map<string, Promise<RefreshOutcome>>();
/** Proactive refreshes of one session are at least this far apart. */
const MIN_PROACTIVE_GAP_MS = 30_000;
/** When each session last called /auth/refresh (Date.now()). */
const lastServerRefreshAt = new Map<string, number>();

/**
 * Milliseconds before the session may refresh proactively again. A backstop
 * against refresh loops (e.g. a token that keeps looking expired): reactive
 * 401 renewals are not throttled.
 */
export function msUntilProactiveRefreshAllowed(store: SessionTokenStore): number {
  const last = lastServerRefreshAt.get(store.key);
  return last === undefined ? 0 : Math.max(0, last + MIN_PROACTIVE_GAP_MS - Date.now());
}

/**
 * When to renew a token that expires in `msLeft`: a minute early, but never
 * earlier than half its remaining life, so a very short JWT_EXPIRY cannot turn
 * into a refresh loop. 0 for a token that has already expired.
 */
export function proactiveRefreshDelay(msLeft: number): number {
  return Math.max(0, msLeft - PROACTIVE_REFRESH_LEAD_MS, msLeft / 2);
}

function isUsable(token: string | null): token is string {
  if (!token) return false;
  const ms = msUntilExpiry(token);
  return ms === null || ms > EXPIRY_SKEW_MS;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withCrossTabLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return fn();
  return locks.request(name, fn) as Promise<T>;
}

/**
 * What a renewal attempt achieved.
 *  - renewed: `token` is the session's current access token.
 *  - ended: the session cannot be renewed — no refresh token, the server
 *    refused it, or it may already have been consumed. Sign out.
 *  - transient: nothing was spent (offline, server error, rate limit, or the
 *    server was unreachable). Keep the session and try again later.
 */
export type RefreshOutcome =
  | { status: 'renewed'; token: string }
  | { status: 'ended' }
  | { status: 'transient' };

const renewed = (token: string): RefreshOutcome => ({ status: 'renewed', token });
const ENDED: RefreshOutcome = { status: 'ended' };
const TRANSIENT: RefreshOutcome = { status: 'transient' };

/**
 * A request that failed this fast without a response almost certainly never
 * reached the server (connection refused, DNS, a network still coming back
 * after sleep). Retrying it this soon also stays inside the server's reuse
 * grace window (10s): if the first attempt DID rotate the token, the retry
 * gets a plain 401 — no revocation — and we learn the token is spent.
 */
const FAST_NETWORK_FAILURE_MS = 3_000;
const NETWORK_RETRY_DELAY_MS = 1_500;
const NETWORK_RETRIES = 2;

async function performRefresh(store: SessionTokenStore, staleToken: string | null): Promise<RefreshOutcome> {
  const before = store.read();
  // Someone else (another tab, or an earlier refresh in this one) already
  // renewed it: adopt their token instead of spending the refresh token again.
  if (before.accessToken && before.accessToken !== staleToken && isUsable(before.accessToken)) {
    if (before.refreshToken) store.write({ accessToken: before.accessToken, refreshToken: before.refreshToken });
    return renewed(before.accessToken);
  }
  if (!before.refreshToken) return ENDED;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return TRANSIENT;

  lastServerRefreshAt.set(store.key, Date.now());
  for (let attempt = 0; ; attempt++) {
    const sentAt = Date.now();
    try {
      const res = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken: before.refreshToken, ...(store.refreshBody?.() ?? {}) },
        { timeout: REFRESH_TIMEOUT_MS },
      );
      const data = (res.data?.data ?? {}) as Partial<TokenPair>;
      if (!data.accessToken || !data.refreshToken) return TRANSIENT;
      recordServerClock(data.accessToken);
      return commitRefresh(store, before, { accessToken: data.accessToken, refreshToken: data.refreshToken });
    } catch (error) {
      if (!isAxiosError(error)) return TRANSIENT;

      if (error.response) {
        // 5xx happen before the rotation commits (the server rotates last);
        // 429 and other non-401 answers never touched the token.
        if (error.response.status !== 401) return TRANSIENT;
        // Refused. Unless another tab rotated this very token a moment earlier
        // (browsers without Web Locks) and is about to store the new pair.
        const hasLocks = typeof navigator !== 'undefined' && !!navigator.locks?.request;
        if (!hasLocks) await sleep(LOST_RACE_RECHECK_MS);
        const after = store.read();
        if (after.refreshToken && after.refreshToken !== before.refreshToken && isUsable(after.accessToken)) {
          return renewed(after.accessToken);
        }
        // After a retry, the refusal may mean our own lost first attempt spent it.
        if (attempt > 0) store.forget(before.refreshToken);
        return ENDED;
      }

      // No response at all.
      const failedFast = Date.now() - sentAt < FAST_NETWORK_FAILURE_MS && error.code !== 'ECONNABORTED';
      if (!failedFast) {
        // A timeout / slow failure: the server may have rotated the token and
        // the response was lost. Presenting it again later would be treated as
        // theft (reuse detection revokes every session of the user), so give
        // it up: this session ends quietly instead.
        store.forget(before.refreshToken);
        return ENDED;
      }
      if (attempt >= NETWORK_RETRIES) return TRANSIENT; // unreachable: nothing was spent
      await sleep(NETWORK_RETRY_DELAY_MS);
    }
  }
}

/**
 * Store a refresh result — unless the session changed while the request was
 * in flight (compare-and-set on the refresh token we spent):
 *  - signed out (no refresh token left): the new pair is revoked, not stored,
 *    so a logout racing a refresh cannot bring the session back;
 *  - a different refresh token (a new login): the new pair is discarded;
 *  - same refresh token but a new access token (org switch): keep that access
 *    token, store the rotated refresh token.
 */
function commitRefresh(store: SessionTokenStore, before: StoredTokens, fresh: TokenPair): RefreshOutcome {
  const now = store.read();
  if (now.refreshToken !== before.refreshToken) {
    if (!now.refreshToken) {
      void axios.post(`${API_BASE_URL}/auth/logout`, { refreshToken: fresh.refreshToken }).catch(() => undefined);
      return ENDED;
    }
    return isUsable(now.accessToken) ? renewed(now.accessToken) : ENDED;
  }
  if (now.accessToken !== before.accessToken && isUsable(now.accessToken)) {
    store.write({ accessToken: now.accessToken, refreshToken: fresh.refreshToken });
    return renewed(now.accessToken);
  }
  store.write(fresh);
  return renewed(fresh.accessToken);
}

/**
 * Renew the session's access token (see RefreshOutcome). The only storage it
 * clears is a refresh token that may already be spent (`forget`); callers
 * decide what an ended session means for them.
 */
export function refreshSession(store: SessionTokenStore, staleToken: string | null): Promise<RefreshOutcome> {
  const pending = inFlight.get(store.key);
  if (pending) return pending;
  const run = withCrossTabLock(`waypoint-token-refresh:${store.key}`, () => performRefresh(store, staleToken))
    .catch((): RefreshOutcome => TRANSIENT)
    .finally(() => inFlight.delete(store.key));
  inFlight.set(store.key, run);
  return run;
}

/** Wait this long before retrying a renewal that failed transiently. */
export const TRANSIENT_RETRY_MS = 30_000;

// ── Admin (main TMS) session: localStorage ──────────────────────────────────

export const adminTokenStore: SessionTokenStore = {
  key: 'admin',
  read: () => ({
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  }),
  write: ({ accessToken, refreshToken }) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    if (useAuthStore.getState().accessToken !== accessToken) {
      useAuthStore.setState({ accessToken });
    }
  },
  forget: (refreshToken) => {
    if (localStorage.getItem('refreshToken') === refreshToken) localStorage.removeItem('refreshToken');
  },
  // Keep a multi-org user's switched org through the refresh. Taken from the
  // shared token every request already carries, not this tab's in-memory
  // user: after an org switch in another tab that would be stale, and the
  // refresh would silently move every tab back to the old org.
  refreshBody: () => {
    const org = decodeJwt(localStorage.getItem('accessToken'))?.org;
    return typeof org === 'string' && org ? { activeOrganizationId: org } : {};
  },
};

/**
 * Run `fn` once no refresh of the admin session is in flight, in this tab or
 * another, and keep other refreshes out until it has finished (an async `fn`
 * holds the lock until it settles). For code that reads the admin refresh
 * token to act on it — impersonation stashes it, logout revokes it: read
 * mid-refresh, it would be a token the server is consuming (presenting it
 * later trips reuse detection; revoking it revokes nothing).
 */
export async function withAdminRefreshSettled<T>(fn: () => T | Promise<T>): Promise<T> {
  await inFlight.get(adminTokenStore.key);
  return withCrossTabLock(`waypoint-token-refresh:${adminTokenStore.key}`, async () => fn());
}

/** True when the admin session holds a refresh token (impersonation does not). */
export function canRefreshAdminSession(): boolean {
  return !!localStorage.getItem('refreshToken');
}

export function refreshAdminSession(staleToken: string | null = localStorage.getItem('accessToken')): Promise<RefreshOutcome> {
  return refreshSession(adminTokenStore, staleToken);
}

/**
 * The admin session's access token, renewed first when it has (nearly)
 * expired. For raw fetch() calls, which bypass the axios renewal.
 */
export async function freshAdminAccessToken(): Promise<string | null> {
  const token = localStorage.getItem('accessToken');
  if (!token || isUsable(token) || !canRefreshAdminSession()) return token;
  const outcome = await refreshAdminSession(token);
  return outcome.status === 'renewed' ? outcome.token : token;
}

// ── Portal sessions: per-tab sessionStorage + shared localStorage slot ──────

export interface PortalSessionTokens {
  accessToken: string;
  refreshToken?: string | null;
  impersonated?: boolean;
}

const PORTAL_SLOT_PREFIX = 'waypoint:session-tokens:';
/**
 * A live slot not rewritten for this long belongs to a session nobody is
 * using (an open portal tab renews — and rewrites it — every ~15 minutes):
 * its tokens are removed so they do not linger on a shared device after the
 * tab was closed without logging out.
 */
const PORTAL_SLOT_IDLE_MS = 60 * 60 * 1000;
/** Ended slots (no tokens) are deleted once their refresh token would have expired (7 days). */
const PORTAL_SLOT_MAX_AGE_MS = 8 * 24 * 60 * 60 * 1000;

/** Short, stable id for a string (FNV-1a) — names the slot, is not a secret. */
function lineageId(refreshToken: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < refreshToken.length; i++) {
    h ^= refreshToken.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36) + refreshToken.length.toString(36);
}

/** A live slot holds the session's current pair; an ended one holds no tokens. */
type PortalSlot = (TokenPair & { at: number; ended?: undefined }) | { ended: true; at: number };

function readSlot(slotKey: string): PortalSlot | null {
  try {
    const raw = localStorage.getItem(slotKey);
    return raw ? (JSON.parse(raw) as PortalSlot) : null;
  } catch {
    return null;
  }
}

function endSlot(slotKey: string): void {
  try {
    localStorage.setItem(slotKey, JSON.stringify({ ended: true, at: Date.now() }));
  } catch {
    /* storage unavailable */
  }
}

/** Idle live slots of OTHER sessions lose their tokens; old ended ones are deleted. */
function pruneStalePortalSlots(keep: string): void {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PORTAL_SLOT_PREFIX) || k === keep) continue;
      const slot = readSlot(k);
      const age = now - (slot?.at ?? 0);
      if (!slot || age > PORTAL_SLOT_MAX_AGE_MS) localStorage.removeItem(k);
      else if (!slot.ended && age > PORTAL_SLOT_IDLE_MS) endSlot(k);
    }
  } catch {
    /* storage unavailable */
  }
}

interface PortalStoreEntry {
  store: SessionTokenStore;
  /** Every access token this session has used in this tab. */
  known: Set<string>;
}

const portalStores = new Map<string, PortalStoreEntry>();

function portalEntry(session: PortalSessionTokens): PortalStoreEntry | null {
  if (session.impersonated || !session.refreshToken) return null;
  const key = `portal:${lineageId(session.refreshToken)}`;
  const existing = portalStores.get(key);
  if (existing) {
    existing.known.add(session.accessToken);
    return existing;
  }
  const slotKey = PORTAL_SLOT_PREFIX + key.slice('portal:'.length);
  const known = new Set<string>([session.accessToken]);
  // The newest pair this tab has seen for the session. Once the session has
  // rotated, the login-time pair in sessionStorage is spent and must never be
  // presented again (reuse detection would revoke every session of the user).
  let latest: TokenPair | null = null;
  let ended = false;
  // The slot name is a short hash, so check the slot really is this user's.
  const owner = decodeJwt(session.accessToken)?.sub;
  const store: SessionTokenStore = {
    key,
    read: () => {
      if (ended) return { accessToken: null, refreshToken: null };
      const slot = readSlot(slotKey);
      if (slot?.ended) {
        ended = true;
        return { accessToken: null, refreshToken: null };
      }
      if (slot && decodeJwt(slot.accessToken)?.sub === owner) {
        known.add(slot.accessToken);
        latest = { accessToken: slot.accessToken, refreshToken: slot.refreshToken };
        return latest;
      }
      if (latest) return latest;
      return { accessToken: session.accessToken, refreshToken: session.refreshToken ?? null };
    },
    write: (pair) => {
      known.add(pair.accessToken);
      latest = pair;
      try {
        localStorage.setItem(slotKey, JSON.stringify({ ...pair, at: Date.now() }));
      } catch {
        /* storage unavailable — this tab keeps the pair in `latest` */
      }
      pruneStalePortalSlots(slotKey);
    },
    forget: () => {
      ended = true;
      endSlot(slotKey);
    },
  };
  const entry = { store, known };
  portalStores.set(key, entry);
  pruneStalePortalSlots(slotKey);
  return entry;
}

/**
 * Register a portal session so its requests are renewed transparently.
 * Returns the store, or null for a session that cannot be renewed
 * (impersonation, or no refresh token). Idempotent.
 */
export function registerPortalSession(session: PortalSessionTokens): SessionTokenStore | null {
  return portalEntry(session)?.store ?? null;
}

/** The session's current refresh token — logout must revoke THIS one, not the login-time one. */
export function currentPortalRefreshToken(session: PortalSessionTokens): string | null {
  const entry = portalEntry(session);
  return entry ? entry.store.read().refreshToken : session.refreshToken ?? null;
}

/**
 * End the session's shared token slot (logout). The slot is marked ended
 * rather than removed: a duplicated tab of the same session must learn that
 * the session is over, not fall back to its spent login-time refresh token.
 * Ended slots hold no tokens and are pruned later.
 */
export function clearPortalSession(session: PortalSessionTokens): void {
  const entry = portalEntry(session);
  if (!entry) return;
  entry.store.forget(entry.store.read().refreshToken ?? '');
  portalStores.delete(entry.store.key);
}

/** Latest access token for a portal session token, renewing it first when it has (nearly) expired. For raw fetch() calls that bypass axios. */
export async function freshPortalAccessToken(token: string): Promise<string> {
  const entry = findPortalEntry(token);
  if (!entry) return token;
  const current = entry.store.read().accessToken ?? token;
  if (isUsable(current)) return current;
  const outcome = await refreshSession(entry.store, current);
  return outcome.status === 'renewed' ? outcome.token : current;
}

function findPortalEntry(token: string): PortalStoreEntry | null {
  for (const entry of portalStores.values()) {
    if (entry.known.has(token)) return entry;
  }
  return null;
}

// ── axios integration ───────────────────────────────────────────────────────

export interface RequestAuth {
  store: SessionTokenStore;
  /** The token the request should carry (may differ from the one the caller passed). */
  token: string;
}

/**
 * Work out which session a request belongs to. `explicitToken` is the bearer
 * token the caller set itself (portals do), or null when the request uses the
 * stored admin token.
 */
export function resolveRequestAuth(explicitToken: string | null): RequestAuth | null {
  const adminToken = localStorage.getItem('accessToken');
  if (!explicitToken) {
    return adminToken ? { store: adminTokenStore, token: adminToken } : null;
  }
  const portal = findPortalEntry(explicitToken);
  if (portal) {
    return { store: portal.store, token: portal.store.read().accessToken ?? explicitToken };
  }
  // An explicit header carrying the admin session's own (possibly
  // superseded) token: same user AND same org. Anything else — portal
  // impersonation (another user), or a token scoped to a different org — is
  // left exactly as the caller set it.
  if (adminToken && explicitToken !== adminToken) {
    const explicitClaims = decodeJwt(explicitToken);
    const adminClaims = decodeJwt(adminToken);
    if (
      !explicitClaims?.sub ||
      explicitClaims.sub !== adminClaims?.sub ||
      explicitClaims.org !== adminClaims?.org
    ) {
      return null;
    }
  }
  return adminToken ? { store: adminTokenStore, token: adminToken } : null;
}

/**
 * Whether a 401 rejected the access token itself — renewable with the refresh
 * token — rather than something else (sign out, as before). authMiddleware
 * answers "Token expired" for an expired JWT and "Invalid token" for a bad or
 * revoked one (e.g. after the user logged out on another device). Only those
 * two: a 401 a route raises for its own reasons must not replay the request.
 */
const RENEWABLE_401_MESSAGES = new Set(['Token expired', 'Invalid token']);

export function isRenewableTokenRejection(responseBody: unknown): boolean {
  const message = (responseBody as { error?: { message?: unknown } } | undefined)?.error?.message;
  return typeof message === 'string' && RENEWABLE_401_MESSAGES.has(message);
}

/** Test hook: forget in-flight refreshes and registered portal sessions. */
export function __resetTokenRefreshForTests(): void {
  inFlight.clear();
  lastServerRefreshAt.clear();
  portalStores.clear();
}
